import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './scss/forgotPassword.scss';

const ForgotPassword = () => {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [step, setStep] = useState(1); // 1: phone, 2: token, 3: new password
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [canResend, setCanResend] = useState(false);
    const [countdown, setCountdown] = useState(30);
    const [isResending, setIsResending] = useState(false);
    const [attemptsRemaining, setAttemptsRemaining] = useState(3); // Allow 3 attempts
    const navigate = useNavigate();

    useEffect(() => {
        let timer;
        if (step === 2 && countdown > 0) {
            timer = setInterval(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
        } else if (countdown === 0) {
            setCanResend(true);
        }
        return () => clearInterval(timer);
    }, [step, countdown]);

    useEffect(() => {
        // Check localStorage for existing attempts
        const resetAttempts = JSON.parse(localStorage.getItem('resetAttempts') || '{}');
        const phoneAttempts = resetAttempts[phoneNumber];
        
        if (phoneAttempts) {
            const { attempts, timestamp } = phoneAttempts;
            const timePassed = Date.now() - timestamp;
            
            // Reset attempts after 1 hour
            if (timePassed > 3600000) { // 1 hour in milliseconds
                delete resetAttempts[phoneNumber];
                localStorage.setItem('resetAttempts', JSON.stringify(resetAttempts));
                setAttemptsRemaining(3);
            } else {
                setAttemptsRemaining(Math.max(0, 3 - attempts));
            }
        }
    }, [phoneNumber]);

    const updateAttempts = () => {
        const resetAttempts = JSON.parse(localStorage.getItem('resetAttempts') || '{}');
        const phoneAttempts = resetAttempts[phoneNumber] || { attempts: 0, timestamp: Date.now() };
        
        phoneAttempts.attempts += 1;
        phoneAttempts.timestamp = Date.now();
        
        resetAttempts[phoneNumber] = phoneAttempts;
        localStorage.setItem('resetAttempts', JSON.stringify(resetAttempts));
        
        setAttemptsRemaining(Math.max(0, 3 - phoneAttempts.attempts));
    };

    const handlePhoneSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        // Check if attempts are exhausted
        if (attemptsRemaining <= 0) {
            const resetAttempts = JSON.parse(localStorage.getItem('resetAttempts') || '{}');
            const phoneAttempts = resetAttempts[phoneNumber];
            const timeLeft = Math.ceil((3600000 - (Date.now() - phoneAttempts.timestamp)) / 60000);
            
            setError(`Too many attempts. Please try again in ${timeLeft} minutes.`);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/send_reset_sms.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ phoneNumber }),
            });

            const data = await response.json();

            if (data.success) {
                setMessage('Reset code sent to your phone');
                setStep(2);
            } else {
                updateAttempts(); // Update attempts on failure
                setError(data.message || 'Failed to send reset code');
            }
        } catch (err) {
            updateAttempts(); // Update attempts on error
            setError('An error occurred. Please try again.');
        }
    };

    const handleTokenSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/verify_reset_token.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    phoneNumber,
                    reset_token: resetToken 
                }),
            });

            const data = await response.json();

            if (data.success) {
                setMessage('Token verified successfully. Please enter your new password.');
                setStep(3); // Move to password reset
            } else {
                setError(data.message || 'Invalid reset code');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        }
    };

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/reset_password.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    phoneNumber,
                    reset_token: resetToken,
                    new_password: newPassword
                }),
            });

            const data = await response.json();

            if (data.success) {
                setMessage('Password reset successfully. Redirecting to login...');
                setTimeout(() => {
                    navigate('/');
                }, 2000);
            } else {
                setError(data.message || 'Failed to reset password');
            }
        } catch (err) {
            setError('An error occurred. Please try again.');
        }
    };

    const handleResendCode = async () => {
        // Check if attempts are exhausted
        if (attemptsRemaining <= 0) {
            const resetAttempts = JSON.parse(localStorage.getItem('resetAttempts') || '{}');
            const phoneAttempts = resetAttempts[phoneNumber];
            const timeLeft = Math.ceil((3600000 - (Date.now() - phoneAttempts.timestamp)) / 60000);
            
            setError(`Too many attempts. Please try again in ${timeLeft} minutes.`);
            return;
        }

        setIsResending(true);
        setError('');
        setMessage('');
        
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/send_reset_sms.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ phoneNumber }),
            });

            const data = await response.json();

            if (data.success) {
                setMessage('New reset code sent to your phone');
                setCanResend(false);
                setCountdown(30);
            } else {
                updateAttempts(); // Update attempts on failure
                setError(data.message || 'Failed to send reset code');
            }
        } catch (err) {
            updateAttempts(); // Update attempts on error
            setError('An error occurred. Please try again.');
        } finally {
            setIsResending(false);
        }
    };

    const handleVerifyToken = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/verify_reset_token.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    phoneNumber, 
                    reset_token: resetToken 
                }),
            });

            const data = await response.json();

            if (data.success) {
                setStep(3);
            } else {
                setError(data.message || 'Failed to verify reset token');
            }
        } catch (error) {
            console.error('Error verifying token:', error);
            setError('An error occurred while verifying the token');
        }
    };

    return (
        <div className="forgot-password-container">
            <h2>Reset Password</h2>
            
            {error && <div className="error-message">{error}</div>}
            {message && <div className="success-message">{message}</div>}
            {attemptsRemaining < 3 && attemptsRemaining > 0 && (
                <div className="attempts-warning">
                    {attemptsRemaining} attempts remaining
                </div>
            )}

            {step === 1 && (
                <form onSubmit={handlePhoneSubmit}>
                    <div className="form-group">
                        <label htmlFor="phoneNumber">Phone Number</label>
                        <input
                            type="tel"
                            id="phoneNumber"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            placeholder="Enter your phone number"
                            required
                        />
                    </div>
                    <button type="submit">Send Reset Code</button>
                </form>
            )}

            {step === 2 && (
                <form onSubmit={handleTokenSubmit}>
                    <div className="form-group">
                        <label htmlFor="resetToken">Reset Code</label>
                        <input
                            type="text"
                            id="resetToken"
                            value={resetToken}
                            onChange={(e) => setResetToken(e.target.value)}
                            placeholder="Enter reset code"
                            required
                        />
                    </div>
                    <button type="submit">Verify Code</button>
                    
                    <div className="resend-section">
                        {!canResend ? (
                            <p className="countdown">Resend code in {countdown}s</p>
                        ) : (
                            <>
                                {attemptsRemaining > 0 ? (
                                    <button 
                                        type="button"
                                        className="resend-button"
                                        onClick={handleResendCode}
                                        disabled={isResending}
                                    >
                                        {isResending ? 'Sending...' : 'Resend Code'}
                                    </button>
                                ) : (
                                    <p className="error-message">Maximum attempts reached</p>
                                )}
                            </>
                        )}
                    </div>
                </form>
            )}

            {step === 3 && (
                <form onSubmit={handlePasswordReset}>
                    <div className="form-group">
                        <label htmlFor="newPassword">New Password</label>
                        <input
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                            required
                        />
                    </div>
                    <button type="submit">Reset Password</button>
                </form>
            )}

            <button 
                className="back-to-login"
                onClick={() => navigate('/')}
            >
                Back to Login
            </button>
        </div>
    );
};

export default ForgotPassword; 